import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { SystemConfig } from '../../types';
import { useAuth } from './AuthContext';
import { useTheme } from './ThemeContext';
import { getCsrfToken } from '../lib/csrf';
import { useDataRefresh } from '../hooks/useDataRefresh';

interface ConfigContextType {
  config: SystemConfig;
  updateConfig: (newConfig: Partial<SystemConfig>, currentPassword: string) => Promise<void>;
  /** Re-lee /api/system-config del servidor sin pasar por updateConfig -- usado tras rotar el
   * JWT_SECRET, que cambia `jwtSecretRotatedAt` sin que the form haya tocado ningún campo. */
  refreshConfig: () => Promise<void>;
  trm: number;
  updateTrm: (newTrm: number) => Promise<void>;
  refreshTrm: () => Promise<void>;
  isLoading: boolean;
}

const defaultConfig: SystemConfig = {
  id: 'default',
  appName: 'Gestión Logística',
  logoUrl: '/logos/logo.png',
  loginLogoUrl: '/logos/logo-inicio.png',
  companyName: 'Logis Trade Express',
  primaryColor: '#003B71',
  secondaryColor: '#4F46E5',
  darkPrimaryColor: '#60A5FA',
  darkSecondaryColor: '#818CF8',
  darkSidebarTheme: 'dark',
  darkMode: false,
  fontFamily: 'Inter',
  borderRadius: 'rounded-2xl',
  tableDensity: 'normal',
  sidebarTheme: 'dark',
  themePreset: 'logis-navy',
  pdfTheme: 'modern',
  pdfLogoAlignment: 'right',
  tenant_id: 'default',
  nit: '901.359.973-1',
  address: 'CL 138 73 52 CA 16',
  phone: '(1) 3205621248',
  city: 'Bogotá - Colombia',
  validityDays: 8,
  transitTime: '5 dias habiles',
  commercialConditions: 'Pago por anticipado del servicio. Abonar a Cuenta Ahorros Bancolombia 17200000811 nombre de Logis Trade Express/ ePayco con recargo del 6%. Favor enviar comprobante de pago a jorgemolanop@gmail.com / Tarifa estimada, variación en pesos, TRM, medidas y otros recargos al arribar al destino se cobraran los cargos adicionales.',
  bankInfo: 'Cuenta Ahorros Bancolombia 17200000811',
  paymentEmail: 'jorgemolanop@gmail.com',
  website: 'www.logistrade.co',
  concurrentSessions: false,
  extendedAudit: false,
  twoFactorAuth: false,
  passwordExpirationDays: 90,
  minPasswordLength: 8,
  lockoutAttempts: 5,
  smtpHost: '',
  smtpPort: 587,
  smtpUser: '',
  smtpPass: '',
  smtpFrom: '',
  smtpSecure: false,
  quoteNotificationEmail: 'operaciones@logistrade.co',
  // Vacíos a propósito (AUD-037, 12/08). Antes traían un host con IP cruda, un usuario y la
  // contraseña 'hola123' de un servicio de desarrollo ya desaparecido -- credenciales muertas
  // (verificado: no coinciden con las activas) pero igualmente compiladas dentro del bundle que
  // se sirve al navegador.
  //
  // Además eran un riesgo latente, no solo mala higiene: `defaultConfig` se fusiona con lo que
  // Returns la API tomando SOLO las entradas no nulas (`{...defaultConfig, ...nonNullEntries}`),
  // así que si la API omitiera `upsPass`, the value by default sobrevivía hasta the form y
  // un guardado lo habría escrito como contraseña real de UPS. Con la cadena vacía eso no puede
  // pasar: the backend solo re-cifra `upsPass` si llega con valor (`if (upsPass && ...)`), de modo
  // que vacío conserva lo que ya estaba guardado.
  upsUrl: '',
  upsUser: '',
  upsPass: '',
  upsEnabledTracking: true,
  upsEnabledQuotes: true,
  upsEnabledBilling: true,
  upsBillingName: '',
  hcaptchaSiteKey: ''
};

// Algunos campos de configuracion_sistema quedaron NULL en filas antiguas tras
// migraciones de esquema que agregaron columnas sin backfill (ej. primaryColor,
// appName) -- un reemplazo directo de estado (`setConfig(data)`) propaga esos
// null a la UI (botones sin color de fondo, textos sin color, etc.). instead of
// reemplazar, se conservan los defaults para cualquier campo que the server
// devuelva como null/undefined.
function mergeWithDefaults(data: Partial<SystemConfig> | null | undefined): SystemConfig {
  if (!data) return defaultConfig;
  const nonNullEntries = Object.entries(data).filter(([, v]) => v !== null && v !== undefined);
  return { ...defaultConfig, ...Object.fromEntries(nonNullEntries) };
}

export function applySystemThemeToDOM(cfg: Partial<SystemConfig>, forceDarkModeOverride: boolean = false) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  // Determinar el modo activo del usuario (dark o light)
  let isDark = root.classList.contains('dark');
  if (forceDarkModeOverride && cfg.darkMode !== undefined && cfg.darkMode !== null) {
    isDark = Boolean(cfg.darkMode);
  } else if (!forceDarkModeOverride) {
    try {
      const userStored = localStorage.getItem('logistrade_theme');
      if (userStored === 'dark' || userStored === 'light') {
        isDark = userStored === 'dark';
      } else if (cfg.darkMode !== undefined && cfg.darkMode !== null) {
        isDark = Boolean(cfg.darkMode);
      }
    } catch (e) {}
  }

  // Resolver paleta according to modo activo
  const primary = isDark
    ? (cfg.darkPrimaryColor || '#60A5FA')
    : (cfg.primaryColor || '#003B71');
  const secondary = isDark
    ? (cfg.darkSecondaryColor || '#818CF8')
    : (cfg.secondaryColor || '#4F46E5');
  const activeSidebarTheme = isDark
    ? (cfg.darkSidebarTheme || 'dark')
    : (cfg.sidebarTheme || 'dark');

  root.style.setProperty('--primary-color', primary);
  root.style.setProperty('--secondary-color', secondary);

  const font = cfg.fontFamily || 'Inter';
  root.style.setProperty('--font-family', `'${font}', sans-serif`);
  document.body.style.fontFamily = `'${font}', ui-sans-serif, system-ui, sans-serif`;

  const radiusMap: Record<string, string> = {
    'rounded-md': '0.375rem',
    'rounded-xl': '0.75rem',
    'rounded-2xl': '1rem',
    'rounded-3xl': '1.5rem',
  };
  const radiusVal = radiusMap[cfg.borderRadius || 'rounded-2xl'] || '1rem';
  root.style.setProperty('--app-border-radius', radiusVal);

  let sidebarBg = '#0F172A';
  let sidebarText = '#F8FAFC';
  if (activeSidebarTheme === 'brand') {
    sidebarBg = primary;
    sidebarText = '#FFFFFF';
  } else if (activeSidebarTheme === 'light') {
    sidebarBg = '#FFFFFF';
    sidebarText = '#0F172A';
  }
  root.style.setProperty('--sidebar-bg', sidebarBg);
  root.style.setProperty('--sidebar-text', sidebarText);

  root.setAttribute('data-table-density', cfg.tableDensity || 'normal');

  // Aplicar clase dark
  if (forceDarkModeOverride) {
    root.classList.toggle('dark', isDark);
    try {
      localStorage.setItem('logistrade_theme', isDark ? 'dark' : 'light');
    } catch (e) {}
  } else {
    root.classList.toggle('dark', isDark);
  }
}

/** Resuelve la paleta de colores correcta according to el modo activo (dark/light).
 * Componentes como Layout.tsx y Login.tsx lo usan para aplicar el color primario/secundario
 * sin hardcodear un solo set — cada modo tiene su propia configuración. */
export function getResolvedThemeColors(cfg: Partial<SystemConfig>, currentTheme: 'dark' | 'light') {
  const isDark = currentTheme === 'dark';
  return {
    primaryColor: isDark ? (cfg.darkPrimaryColor || '#60A5FA') : (cfg.primaryColor || '#003B71'),
    secondaryColor: isDark ? (cfg.darkSecondaryColor || '#818CF8') : (cfg.secondaryColor || '#4F46E5'),
    sidebarTheme: (isDark ? (cfg.darkSidebarTheme || 'dark') : (cfg.sidebarTheme || 'dark')) as 'dark' | 'brand' | 'light',
  };
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

/**
 * Roles que pueden forzar el refresco de la TRM contra la fuente externa.
 *
 * Espejo de `FINANCE_ROLES` en `server.ts`, que es quien de verdad decide. Esto
 * NO es el control de acceso -- the server sigue rechazando a quien no
 * corresponda -- sino la forma de no pedir lo que ya se sabe que va a negarse.
 * Si la lista del servidor cambia, aquí solo se pierde la optimización: el
 * respaldo por valor guardado sigue funcionando.
 */
const ROLES_REFRESCO_TRM = ['adminapp', 'admin', 'ceo', 'accounting'];

export const ConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const puedeRefrescarTrm = Boolean(user?.roles?.some((r) => ROLES_REFRESCO_TRM.includes(r)));
  const { theme } = useTheme();
  const [config, setConfig] = useState<SystemConfig>(defaultConfig);
  const [trm, setTrm] = useState<number>(4000);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    applySystemThemeToDOM(config);
  }, [config, theme]);

  /** Lee la TRM guardada. Disponible para cualquier sesión (`GET /api/config/trm`). */
  const leerTrmGuardada = useCallback(async () => {
    try {
      const trmRes = await fetch('/api/config/trm');
      if (trmRes.ok) {
        const data = await trmRes.json();
        if (data.rate) setTrm(data.rate);
      }
    } catch (e) {
      // Sin TRM guardada tampoco: se conserva the value en memoria. No hay nada
      // que the user pueda hacer al respecto, así que no se le interrumpe.
    }
  }, []);

  const fetchTrm = useCallback(async () => {
    if (!isAuthenticated) return;

    // `POST /api/config/trm/refresh` exige FINANCE_ROLES en the server. Un
    // usuario con rol `cliente` recibía 403 EN CADA Loads DE PÁGINA del portal, y
    // el `catch` lo escribía en consola: siete rutas × cada navegación (UX_AUDIT
    // H-07). Eso dejaba la consola inservible para diagnosticar -- cualquier error
    // nuevo se perdía entre el ruido -- y además convertía un caso perfectamente
    // normal ("este rol no refresca tasas de cambio") en algo que parecía un fallo.
    //
    // Pedir permiso primero, instead of pedir perdón después: quien no puede
    // refrescar lee directamente the value guardado, que es justo lo que necesita.
    if (!puedeRefrescarTrm) {
      await leerTrmGuardada();
      return;
    }

    try {
      // the server decide si toca refrescar desde la fuente externa (con circuit breaker
      // + retry) o servir el último valor cacheado; nunca sobrescribe con un default si la
      // fuente externa falla.
      const response = await fetch('/api/config/trm/refresh', {
        method: 'POST',
        headers: { 'X-CSRF-Token': getCsrfToken() }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.rate) setTrm(data.rate);
        return;
      }
      throw new Error(`refresh endpoint devolvió ${response.status}`);
    } catch (error) {
      // Aquí sí es un fallo real: este usuario TIENE permiso y aun así no se pudo
      // refrescar. Se avisa una vez y se cae al último valor guardado.
      console.warn('No se pudo refrescar la TRM; se usa el último valor guardado:', error);
      await leerTrmGuardada();
    }
  }, [isAuthenticated, puedeRefrescarTrm, leerTrmGuardada]);

  const fetchConfig = useCallback(async () => {
    try {
      // Try fetching the authenticated (full) config first if there's a session
      if (isAuthenticated) {
        const response = await fetch('/api/system-config');
        if (response.ok) {
          const data = await response.json();
          if (data) {
            setConfig(mergeWithDefaults(data));
            setIsLoading(false);
            return;
          }
        }
      }

      // Fallback to public config
      const publicResponse = await fetch('/api/system-config/public');
      if (publicResponse.ok) {
        const publicData = await publicResponse.json();
        if (publicData) setConfig(mergeWithDefaults(publicData));
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);
  // A nivel de contexto (no solo en pages/Configuracion.tsx) so that TODA la app se entere si
  // otro admin cambia the configuration -- ej. el logo mostrado en el sidebar de cualquier página.
  useDataRefresh(['config'], fetchConfig);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchTrm();
    // Refresco periódico: la acción manual queda solo como respaldo, no como único disparador.
    const interval = setInterval(fetchTrm, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchTrm]);

  // Endurecimiento 26/07: esta pantalla ahora concentra todas las credenciales del sistema
  // (antes repartidas también en .env) -- the backend exige reconfirmar la contraseña propia
  // del usuario autenticado en cada guardado (ver PUT /api/system-config, server.ts).
  const updateConfig = async (newConfig: Partial<SystemConfig>, currentPassword: string) => {
    try {
      const response = await fetch('/api/system-config', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify({ ...config, ...newConfig, currentPassword })
      });

      if (response.ok) {
        // Re-read from the server so redacted secrets, derived flags (*HasCredentials)
        // and CORS/theme fields match what the rest of the app just received over SSE.
        await fetchConfig();
      } else {
        const data = await response.json().catch(() => null);
        throw new Error(data?.error || 'Failed to update config');
      }
    } catch (error) {
      console.error('Error updating config:', error);
      throw error;
    }
  };

  const updateTrm = async (newTrm: number) => {
    try {
      const response = await fetch('/api/config/trm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify({ rate: newTrm })
      });

      if (response.ok) {
        setTrm(newTrm);
      } else {
        throw new Error('Failed to update TRM');
      }
    } catch (error) {
      console.error('Error updating TRM:', error);
      throw error;
    }
  };

  return (
    <ConfigContext.Provider value={{ config, updateConfig, refreshConfig: fetchConfig, trm, updateTrm, refreshTrm: fetchTrm, isLoading }}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};
